import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von05Cardiovascular } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von05CardiovascularFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von05CardiovascularForm: React.FC<Von05CardiovascularFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  const [pda, setPda] = useState<string>('');
  const [ibuprofenPda, setIbuprofenPda] = useState(false);
  const [indomethacinAny, setIndomethacinAny] = useState(false);
  const [acetaminPda, setAcetaminPda] = useState(false);
  const [pdaClosureMethod, setPdaClosureMethod] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getCardiovascular(patientId);

      if (data) {
        setHasExistingData(true);
        setPda(data.pda?.toString() || '');
        setIbuprofenPda(Boolean(data.ibuprofen_pda));
        setIndomethacinAny(Boolean(data.indomethacin_any));
        setAcetaminPda(Boolean(data.acetamin_pda));
        setPdaClosureMethod(data.pda_closure_method?.toString() || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von05Cardiovascular> = {
        patient_id: patientId,
        pda: pda ? parseInt(pda) : undefined,
        ibuprofen_pda: ibuprofenPda,
        indomethacin_any: indomethacinAny,
        acetamin_pda: acetaminPda,
        pda_closure_method: pdaClosureMethod ? parseInt(pdaClosureMethod) : undefined
      };

      if (hasExistingData) {
        await vonDataService.updateCardiovascular(patientId, formData);
      } else {
        await vonDataService.createCardiovascular(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 05: Cardiovascular</CardTitle>
        <CardDescription>Date despre PDA și tratamente cardiovasculare</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* PDA */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Canal arterial persistent (PDA)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pda">Canal arterial persistent</Label>
                <Select value={pda} onValueChange={setPda} disabled={readOnly}>
                  <SelectTrigger id="pda">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {pda === '1' && (
                <div className="space-y-2">
                  <Label htmlFor="pdaClosureMethod">Metodă închidere PDA</Label>
                  <Select value={pdaClosureMethod} onValueChange={setPdaClosureMethod} disabled={readOnly}>
                    <SelectTrigger id="pdaClosureMethod">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Medical</SelectItem>
                      <SelectItem value="2">Cateterism intervențional</SelectItem>
                      <SelectItem value="3">Chirurgical</SelectItem>
                      <SelectItem value="4">Spontan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* PDA treatments */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Tratamente pentru PDA</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ibuprofenPda"
                  checked={ibuprofenPda}
                  onCheckedChange={(checked) => setIbuprofenPda(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ibuprofenPda" className="text-sm font-normal cursor-pointer">
                  Ibuprofen pentru PDA
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="indomethacinAny"
                  checked={indomethacinAny}
                  onCheckedChange={(checked) => setIndomethacinAny(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="indomethacinAny" className="text-sm font-normal cursor-pointer">
                  Indometacin (orice motiv)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="acetaminPda"
                  checked={acetaminPda}
                  onCheckedChange={(checked) => setAcetaminPda(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="acetaminPda" className="text-sm font-normal cursor-pointer">
                  Acetaminofen pentru PDA
                </Label>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
